/**
 * Toast globali — riscontro visivo dopo le azioni ("✓ Salvato", errori).
 *
 * Store esterno minimale (niente dipendenze): qualunque punto del codice può
 * chiamare `toast.success(...)` / `toast.error(...)` senza hook. Il
 * componente <Toaster /> va montato una volta sola (vedi main.tsx).
 *
 * I toast scompaiono da soli; si possono chiudere con un click.
 */
import { useSyncExternalStore } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, CircleAlert, Info, X } from 'lucide-react';

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

let items: ToastItem[] = [];
const listeners = new Set<() => void>();
let nextId = 1;

function emit() {
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): ToastItem[] {
  return items;
}

function dismiss(id: number) {
  items = items.filter((t) => t.id !== id);
  emit();
}

function push(kind: ToastKind, message: string, ttlMs: number): number {
  const id = nextId++;
  items = [...items, { id, kind, message }];
  emit();
  // setTimeout fuori dal render path: ok.
  setTimeout(() => dismiss(id), ttlMs);
  return id;
}

export const toast = {
  success: (message: string) => push('success', message, 3500),
  error: (message: string) => push('error', message, 5000),
  info: (message: string) => push('info', message, 3500),
  dismiss,
};

function ToastRow({ t }: { t: ToastItem }) {
  const styles =
    t.kind === 'success'
      ? 'bg-emerald-50/95 border-emerald-200 text-emerald-800'
      : t.kind === 'error'
        ? 'bg-red-50/95 border-red-200 text-red-700'
        : 'bg-card/95 border-border text-foreground';

  const Icon = t.kind === 'success' ? Check : t.kind === 'error' ? CircleAlert : Info;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.98 }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      onClick={() => toast.dismiss(t.id)}
      role="status"
      aria-live="polite"
      className={
        'pointer-events-auto cursor-pointer flex items-center gap-2.5 rounded-lg border ' +
        'px-4 py-3 shadow-lg backdrop-blur max-w-[92vw] sm:max-w-md text-sm ' +
        styles
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
      <span className="font-medium min-w-0">{t.message}</span>
      <X className="h-3.5 w-3.5 shrink-0 opacity-50 ml-1" strokeWidth={2} />
    </motion.div>
  );
}

export function Toaster() {
  const list = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return (
    <div className="fixed inset-x-0 top-3 sm:top-4 z-[100] flex flex-col items-center gap-2 px-3 pointer-events-none">
      <AnimatePresence initial={false}>
        {list.map((t) => (
          <ToastRow key={t.id} t={t} />
        ))}
      </AnimatePresence>
    </div>
  );
}
